import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Server, AlertCircle, CheckCircle } from 'lucide-react';
import type { EBSConnectionConfig } from '../types/config';

interface EBSConfigFormProps {
  platform?: string;
  onTestConnection?: () => Promise<void>;
  testResult?: { success: boolean; message: string };
}

export const EBSConfigForm: React.FC<EBSConfigFormProps> = ({ platform = 'EBS', onTestConnection, testResult }) => {
  const { config, updateEBS } = useConfigStore();
  const ebs = config.ebs;

  // Get platform-specific title and labels
  const getPlatformTitle = () => {
    if (platform === 'EBS') return 'Oracle EBS Configuration';
    if (platform === 'FUSION') return 'Oracle Fusion Cloud Configuration';
    if (platform === 'NETSUITE') return 'Oracle NetSuite Configuration';
    if (platform === 'JDE') return 'Oracle JD Edwards Configuration';
    if (platform === 'PSFT') return 'Oracle PeopleSoft Configuration';
    return 'Platform Configuration';
  };

  const getPlatformDescription = () => {
    if (platform === 'EBS') return 'Configure connection to Oracle E-Business Suite';
    if (platform === 'FUSION') return 'Configure connection to Oracle Fusion Cloud ERP';
    if (platform === 'NETSUITE') return 'Configure connection to Oracle NetSuite';
    if (platform === 'JDE') return 'Configure connection to Oracle JD Edwards EnterpriseOne';
    if (platform === 'PSFT') return 'Configure connection to Oracle PeopleSoft';
    return 'Configure platform connection';
  };

  const handleUpdate = (updates: Partial<EBSConnectionConfig>) => {
    updateEBS(updates);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Server className="w-5 h-5 text-blue-600" />
        <div>
          <h3 className="text-lg font-semibold">{getPlatformTitle()}</h3>
          <p className="text-sm text-gray-600">{getPlatformDescription()}</p>
        </div>
      </div>

      {/* Connection Settings */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Connection Settings</h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
            <input
              type="text"
              value={ebs.host}
              onChange={(e) => handleUpdate({ host: e.target.value })}
              placeholder="e.g., ebs.example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">EBS server hostname or IP address</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
            <input
              type="number"
              value={ebs.port}
              onChange={(e) => handleUpdate({ port: parseInt(e.target.value) })}
              placeholder="8000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">EBS application server port (typically 8000)</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={ebs.username}
              onChange={(e) => handleUpdate({ username: e.target.value })}
              placeholder="e.g., sysadmin"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">EBS system administrator username</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={ebs.password}
              onChange={(e) => handleUpdate({ password: e.target.value })}
              placeholder="••••••••"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* EBS Instance Settings */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">EBS Instance Settings</h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instance Name</label>
            <input
              type="text"
              value={ebs.instanceName}
              onChange={(e) => handleUpdate({ instanceName: e.target.value })}
              placeholder="e.g., PROD"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">EBS instance identifier (e.g., PROD, TEST)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Responsibility</label>
            <select
              value={ebs.responsibility}
              onChange={(e) => handleUpdate({ responsibility: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="System Administrator">System Administrator</option>
              <option value="General Ledger Super User">General Ledger Super User</option>
              <option value="Accounts Payable Super User">Accounts Payable Super User</option>
              <option value="Accounts Receivable Super User">Accounts Receivable Super User</option>
              <option value="Purchasing Super User">Purchasing Super User</option>
              <option value="Inventory Super User">Inventory Super User</option>
              <option value="Order Management Super User">Order Management Super User</option>
              <option value="Custom">Custom</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">EBS user responsibility for API access</p>
          </div>
        </div>
      </div>

      {/* Connection Options */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Connection Options</h4>
        
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="ebsssl"
            checked={ebs.ssl}
            onChange={(e) => handleUpdate({ ssl: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="ebsssl" className="text-sm font-medium text-gray-700">
            Use SSL/TLS Connection
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Connection Timeout (ms)</label>
            <input
              type="number"
              value={ebs.timeout}
              onChange={(e) => handleUpdate({ timeout: parseInt(e.target.value) })}
              min="1000"
              step="1000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Connection timeout in milliseconds</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retry Attempts</label>
            <input
              type="number"
              value={ebs.retryAttempts}
              onChange={(e) => handleUpdate({ retryAttempts: parseInt(e.target.value) })}
              min="0"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Number of retry attempts on failure</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (ms)</label>
            <input
              type="number"
              value={ebs.retryDelay}
              onChange={(e) => handleUpdate({ retryDelay: parseInt(e.target.value) })}
              min="100"
              step="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Delay between retry attempts</p>
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Advanced Settings</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OAF Page URL (Optional)</label>
            <input
              type="url"
              value={ebs.oafPageUrl || ''}
              onChange={(e) => handleUpdate({ oafPageUrl: e.target.value })}
              placeholder="https://ebs.example.com/OA_HTML/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">URL to Oracle Application Framework page for invoice status</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Web Service URL (Optional)</label>
            <input
              type="url"
              value={ebs.webServiceUrl || ''}
              onChange={(e) => handleUpdate({ webServiceUrl: e.target.value })}
              placeholder="https://ebs.example.com/webservices/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">URL to EBS web services for API calls</p>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      {onTestConnection && (
        <button
          onClick={onTestConnection}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Test {platform} Connection
        </button>
      )}

      {testResult && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>{testResult.message}</p>
        </div>
      )}
    </div>
  );
};
